import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { supabase } from '../../lib/supabase';
import { openGmailCompose } from '../../lib/gmailComposeUrl';
import {
  appendGmailConfirmSignature,
  loadGmailConfirmSignature,
  saveGmailConfirmSignature,
} from '../../lib/gmailConfirmSignature';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalPanel,
} from '../Common/ModalShell';

interface ConfirmGmailDraftModalProps {
  isOpen: boolean;
  email: string;
  onClose: () => void;
  onDone: () => void;
}

export default function ConfirmGmailDraftModal({
  isOpen,
  email,
  onClose,
  onDone,
}: ConfirmGmailDraftModalProps) {
  const [rotateToken, setRotateToken] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [sendingSmtp, setSendingSmtp] = useState(false);
  const [to, setTo] = useState(email);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirmUrl, setConfirmUrl] = useState('');
  const [signature, setSignature] = useState('');
  const [draftReady, setDraftReady] = useState(false);

  /** SMTP/editor body + signature (keeps "Confirm here" as words for HTML linking). */
  const fullBodyForSmtp = () => appendGmailConfirmSignature(body, signature);

  /** Open-in-Gmail must be plain text — expand Confirm here with the real URL. */
  const fullBodyForCompose = () => {
    let text = body;
    if (confirmUrl.trim()) {
      if (text.includes(confirmUrl.trim())) {
        // already present
      } else if (/confirm here/i.test(text)) {
        text = text.replace(/confirm here/i, `confirm here: ${confirmUrl.trim()}`);
      } else {
        text = `${text}\n\n${confirmUrl.trim()}`;
      }
    }
    return appendGmailConfirmSignature(text, signature);
  };

  const loadDraft = useCallback(async (rotate = rotateToken) => {
    if (!email.trim()) return;
    setLoadingDraft(true);
    setDraftReady(false);
    try {
      const { data, error } = await supabase.functions.invoke('llc-send-confirm-gmail', {
        body: { email, rotate_token: rotate, delivery: 'compose' },
      });
      if (error) {
        const msg =
          (data as { error?: string } | null | undefined)?.error ||
          error.message ||
          'Could not prepare confirmation draft';
        throw new Error(msg);
      }
      const draft = data as {
        to?: string;
        subject?: string;
        text?: string;
        confirm_url?: string;
        token_rotated?: boolean;
      } | null;
      const nextTo = typeof draft?.to === 'string' && draft.to.trim() ? draft.to.trim() : email;
      const nextSubject =
        typeof draft?.subject === 'string' && draft.subject.trim()
          ? draft.subject.trim()
          : 'Confirm your subscription';
      const nextBody =
        typeof draft?.text === 'string' && draft.text.trim() ? draft.text.trim() : '';
      const nextConfirmUrl =
        typeof draft?.confirm_url === 'string' ? draft.confirm_url.trim() : '';
      if (!nextBody) throw new Error('No confirmation draft returned');
      if (!nextConfirmUrl) throw new Error('No confirm URL returned');
      setTo(nextTo);
      setSubject(nextSubject);
      setBody(nextBody);
      setConfirmUrl(nextConfirmUrl);
      setDraftReady(true);
      if (draft?.token_rotated) {
        toast.message('New confirm link issued — older links won’t work');
      }
    } catch (e: unknown) {
      reportUiError('subscribers', 'prepare_confirm_gmail_draft', e);
      toast.error(e instanceof Error ? e.message : 'Could not prepare confirmation draft');
    } finally {
      setLoadingDraft(false);
    }
  }, [email, rotateToken]);

  useEffect(() => {
    if (!isOpen) return;
    setRotateToken(false);
    setTo(email);
    setSubject('');
    setBody('');
    setConfirmUrl('');
    setSignature(loadGmailConfirmSignature());
    setDraftReady(false);
    void loadDraft(false);
    // Open-only reset + prepare with rotate=false; Reload uses the checkbox.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional open-only prepare
  }, [isOpen, email]);

  const persistSignature = () => {
    saveGmailConfirmSignature(signature);
    toast.success('Signature saved for next time');
  };

  const handleOpenInGmail = () => {
    if (!draftReady || !body.trim()) return;
    saveGmailConfirmSignature(signature);
    openGmailCompose({
      to: to || email,
      subject: subject.trim(),
      body: fullBodyForCompose(),
    });
    toast.success('Opened Gmail compose — plain text only; use Send via SMTP for the HTML link');
    onDone();
    onClose();
  };

  const handleSendViaSmtp = async () => {
    if (!draftReady || !body.trim() || !subject.trim() || !confirmUrl.trim()) return;
    setSendingSmtp(true);
    try {
      saveGmailConfirmSignature(signature);
      const { data, error } = await supabase.functions.invoke('llc-send-confirm-gmail', {
        body: {
          email,
          rotate_token: false,
          delivery: 'smtp',
          subject: subject.trim(),
          text: fullBodyForSmtp(),
          confirm_url: confirmUrl.trim(),
        },
      });
      if (error) {
        const msg =
          (data as { error?: string } | null | undefined)?.error ||
          error.message ||
          'Could not send confirmation via Gmail';
        throw new Error(msg);
      }
      const from =
        typeof (data as { from?: string } | null)?.from === 'string'
          ? (data as { from: string }).from
          : 'Gmail';
      toast.success(`Confirmation sent via Gmail (${from}) to ${email}`);
      onDone();
      onClose();
    } catch (e: unknown) {
      reportUiError('subscribers', 'send_confirm_gmail', e);
      toast.error(e instanceof Error ? e.message : 'Could not send confirmation via Gmail');
    } finally {
      setSendingSmtp(false);
    }
  };

  if (!isOpen) return null;

  const busy = loadingDraft || sendingSmtp;

  return (
    <ModalOverlay onClose={busy ? undefined : onClose} closeOnBackdropClick={!busy}>
      <ModalPanel maxWidthClass="max-w-2xl">
        <ModalHeader
          titleId="confirm-gmail-draft-title"
          title="Confirm via Gmail"
          description={
            <>
              Edit the draft for{' '}
              <span className="font-mono text-xs break-all">{email}</span>. Keep the words
              “confirm here” — <span className="font-medium">Send via SMTP</span> turns that into
              a clickable HTML link. Open in Gmail is plain text only.
            </>
          }
          onClose={busy ? undefined : onClose}
        />
        <ModalBody className="space-y-4">
          <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={rotateToken}
              onChange={(e) => setRotateToken(e.target.checked)}
              disabled={busy}
              className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
            />
            <span>
              Issue a <span className="font-medium">new</span> confirm link
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Leave unchecked to reuse the existing link. After changing this, reload the draft.
              </span>
            </span>
          </label>

          {loadingDraft ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Preparing draft…
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={busy || !draftReady}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Body
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  disabled={busy || !draftReady}
                  rows={12}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white font-mono leading-relaxed resize-y min-h-[180px]"
                />
                {confirmUrl ? (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 break-all">
                    Link target (hidden in body):{' '}
                    <span className="font-mono">{confirmUrl}</span>
                  </p>
                ) : null}
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Signature
                  </label>
                  <button
                    type="button"
                    onClick={persistSignature}
                    disabled={busy}
                    className="text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:underline disabled:opacity-50"
                  >
                    Save signature
                  </button>
                </div>
                <textarea
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  disabled={busy}
                  rows={5}
                  placeholder="Paste your Gmail signature (plain text)"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white font-mono leading-relaxed resize-y"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Appended on Open in Gmail and Send via SMTP. Plain text only (Gmail’s HTML
                  signature can’t be pulled in automatically).
                </p>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
          <button
            type="button"
            onClick={() => void loadDraft()}
            disabled={busy}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Reload draft
          </button>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleOpenInGmail}
              disabled={busy || !draftReady || !body.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-emerald-600 text-emerald-800 dark:text-emerald-200 bg-white dark:bg-gray-900 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 disabled:opacity-50"
            >
              <ExternalLink className="w-4 h-4" />
              Open in Gmail
            </button>
            <button
              type="button"
              onClick={() => void handleSendViaSmtp()}
              disabled={busy || !draftReady || !body.trim() || !subject.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {sendingSmtp ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send via SMTP
            </button>
          </div>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
}
